import { GridOffset } from "@insite/mobius/GridContainer";
import { GridItemStyle } from "@insite/mobius/GridItem";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import { COMMON, SPACING, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/Framework/Common/Utilities/breakpointValuesMediaQueries";
import updateCssValue, { Operators } from "@store/Framework/Common/Utilities/updateCssValue";
import last from "lodash/last";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";

const _contentSpacingStyles = (
    unit: string,
    override?: boolean,
    top: string | 0 | null = 0,
    bottom: string | 0 | null = TYPOGRAPHY.spacing.base,
) => {
    return css`
        ${top !== null
            ? `${unit}-top: ${top}; ${top !== 0 ? `&:first-child { ${unit}-top: ${override ? top : 0}; }` : ""}`
            : ""}
        ${bottom !== null
            ? `${unit}-bottom: ${bottom}; ${
                  bottom !== 0 ? `&:last-child { ${unit}-bottom: ${override ? bottom : 0}; }` : ""
              }`
            : ""}
    `;
};

export const contentSpacingStyles = (
    top: string | 0 | null = 0,
    bottom: string | 0 | null = TYPOGRAPHY.spacing.base,
    unit = "margin",
) => {
    return _contentSpacingStyles(unit, false, top, bottom);
};

export const overrideSpacingStyles = (
    top: string | 0 | null = 0,
    bottom: string | 0 | null = TYPOGRAPHY.spacing.base,
    unit = "margin",
) => {
    return _contentSpacingStyles(unit, true, top, bottom);
};

export const siteWidthStyles = (sideSpacing?: Array<string | null>) => {
    const _sideSpacing = sideSpacing ?? COMMON.spacing.side;

    return css`
        max-width: ${props => last(props.theme.breakpoints.maxWidths)}px;
        margin-right: auto;
        margin-left: auto;
        ${props =>
            breakpointValuesMediaQueries(
                props.theme,
                [{ values: _sideSpacing, properties: ["padding-right", "padding-left"] }],
                mediaOptions.min,
            )}
    `;
};

export const contentWidthStyles = () => {
    return css`
        max-width: ${COMMON.width.content}px;
        padding: 0;
        margin-right: auto;
        margin-left: auto;
    `;
};

export const largeContentWidthStyles = () => {
    return css`
        max-width: ${COMMON.width.largeContent}px;
        padding: 0;
        margin-right: auto;
        margin-left: auto;
    `;
};

export const widgetWrapperStyles = () => {
    return css`
        ${contentSpacingStyles()}
    `;
};

export const clearfixStyles = () => {
    return css`
        &::before,
        &::after {
            content: "";
            display: table;
        }

        &::after {
            clear: both;
        }
    `;
};

export const scalableIconStyles = () => {
    return css`
        position: relative;
        width: 1em;
        height: 1em;

        svg {
            width: inherit;
            height: inherit;
        }
    `;
};

export const tagWrapperStyles = () => {
    return css`
        display: flex;
        flex-flow: row wrap;
        margin-right: -10px;
        margin-bottom: -10px;
    `;
};

export const mainContainerPageStyle = () => {
    return css`
        max-width: ${COMMON.width.content}px;
        margin: 0 auto;
    `;
};

export const containerSidePaddingStyle = () => {
    return css`
        padding: 0 ${SPACING.standard};
    `;
};

export const containerShadowStyles = () => {
    return css`
        background: ${getColor("common.background")};
        border: 0;
        box-shadow: 0px 2px 6px 1px rgba(0, 0, 0, 0.08);
    `;
};

export const borderBottomTitlesStyles = () => {
    return css`
        padding-bottom: 20px;
        border-bottom: 1px solid ${getColor("common.border")};
    `;
};

// -- GRID

export const gridGapStyles = (
    horizontalGap: Array<string | 0 | null>,
    verticalGap?: Array<string | 0 | null> | null,
) => {
    const _horizontalGap = horizontalGap.map(x => updateCssValue(x, Operators.Divide, 2));
    const _verticalGap = verticalGap?.map(x => updateCssValue(x, Operators.Divide, 2)) ?? _horizontalGap;

    return css`
        > ${GridOffset} {
            width: auto !important; /* TODO: Remove "!important" once GridOffset issues are fixed */
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        {
                            values: _horizontalGap.map(x => (x ? `-${x}` : x)),
                            properties: ["margin-right", "margin-left"],
                        },
                        {
                            values: _verticalGap.map(x => (x ? `-${x}` : x)),
                            properties: ["margin-top", "margin-bottom"],
                        },
                    ],
                    mediaOptions.min,
                )}

            > ${GridItemStyle} {
                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [
                            { values: _horizontalGap, properties: ["padding-right", "padding-left"] },
                            { values: _verticalGap, properties: ["padding-top", "padding-bottom"] },
                        ],
                        mediaOptions.min,
                    )}
            }
        }
    `;
};
